import React from 'react';

import {AnalysisGraphQL, AnalysisViewGraphQL} from 'analysis-tool/AnalysisTypes';
import getLegacyStepFromAnalysis from 'analysis-tool/data/getStepFromAnalysis';
import {getOutputDataForView} from 'analysis-tool/data-view/AnalysisViewUtils';
import {checkCanModifyStep} from 'analysis-tool/EditStepHelpers';
import {PipelineStepDataKind} from 'graphql-client/__generated/graphqlGlobalTypes';
import hasDownstreamFlagRowsStep from 'pipelines/hasDownstreamFlagRowsStep';
import {pipelineStepRawDataIsKind} from 'pipelines/PipelineTypes';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';

export interface ViewLockedWarningBannerProps {
  analysis: AnalysisGraphQL;
  view: AnalysisViewGraphQL;
}

/**
 * Warning banner that renders when the given view cannot be modified.
 *
 * If the view can be modified, returns null.
 *
 * Notably, the modification logic does not check analysis-wide properties, such as if the analysis is
 * readonly. These are expected to be rendered as separate banners in other parts of the UI.
 */
export default function ViewLockedWarningBanner({
  analysis,
  view,
}: ViewLockedWarningBannerProps): JSX.Element | null {
  const outputData = getOutputDataForView(view);
  if (outputData == null || pipelineStepRawDataIsKind(outputData, PipelineStepDataKind.FILE)) {
    return null;
  }

  let message: string | null = null;
  const lastStep = getLegacyStepFromAnalysis(analysis, view.lineage[view.lineage.length - 1].step.id);
  const {canEdit, message: canModifyMessage} = checkCanModifyStep(
    analysis,
    lastStep.id,
    'view',
    false,
    false
  );
  if (!canEdit) {
    message = canModifyMessage;
  } else if (hasDownstreamFlagRowsStep(analysis, lastStep)) {
    // TODO(BNCH-125860): Remove once flag rows is included in can modify step logic
    message = 'This view cannot be modified because one or more downstream views contain flagged rows.';
  }

  return message != null ? (
    <InlineBanner icon="lock" bannerType={InlineBannerType.ATTENTION} text={message} />
  ) : null;
}
